// App shell: top bar + side nav + routed canvas. Mounts react-query +
// react-router. Side-effect: importing ./visuals/registry registers all
// renderers exactly once.
//
// Also exports a tiny <VisualErrorBoundary> used by PageView so a single
// visual renderer throwing during render doesn't bring down the entire
// page; the error is shown inline inside that visual's card.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TopBar } from "./pages/TopBar";
import { SideNav } from "./pages/SideNav";
import { PageView } from "./pages/PageView";
// AGENT-CX: HSQ_LANDING_FOLDER import dropped — the root redirect now points
// at /dashboard. Other modules (TopBar, side nav) still import the constant
// from ./config/hsqPages directly.
// AGENT-R: app-root drawer + its hsq:open-raw-tables window listener.
import { DataDrillDrawer } from "./components/DataDrillDrawer";
// AGENT-A5: new dashboard rebuild lives at /dashboard.
import DashboardApp from "./dashboard/DashboardApp";
import "./visuals/registry";
import "./App.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

// ── Error boundary ─────────────────────────────────────────────────────────
interface VEBProps {
  children: ReactNode;
  /** Optional label shown in the inline error UI (e.g. visual id/type). */
  label?: string;
}
interface VEBState {
  error: Error | null;
}

export class VisualErrorBoundary extends Component<VEBProps, VEBState> {
  state: VEBState = { error: null };

  static getDerivedStateFromError(error: Error): VEBState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[VisualErrorBoundary]", this.props.label ?? "(unlabelled)", error, info);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div
          className="vshell"
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            background: "#fff5f5",
            border: "1px solid #f5c6cb",
            color: "#721c24",
            padding: 8,
            boxSizing: "border-box",
            fontFamily: "var(--font-family)",
            fontSize: 11,
            overflow: "auto",
            borderRadius: 4,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Visual failed to render{this.props.label ? `: ${this.props.label}` : ""}
          </div>
          <div style={{ whiteSpace: "pre-wrap" }}>
            {this.state.error.message || String(this.state.error)}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// AGENT-CX: Default landing route now goes to /dashboard (the rebuilt view).
// The legacy /p/{HSQ_LANDING_FOLDER} URL remains reachable for the "Legacy
// view" link in the dashboard top bar.

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* AGENT-A5: standalone /dashboard route — renders the rebuild's
              own shell (TopBar + DashboardLayout). Mounted outside the
              legacy app-shell so it doesn't inherit the SideNav / legacy
              TopBar. The DataDrillDrawer still listens via window event. */}
          <Route
            path="/dashboard"
            element={
              <>
                <DashboardApp />
                <DataDrillDrawer />
              </>
            }
          />
          {/* Legacy routes — unchanged. */}
          <Route
            path="*"
            element={
              <div className="app-shell">
                {/* AGENT-L: TopBar now carries the "HSQ" title + breadcrumb +
                    "View Raw Tables" button. Title is hardcoded inside TopBar. */}
                <TopBar />
                <div className="app-body">
                  <SideNav />
                  <main className="app-main">
                    <Routes>
                      {/* AGENT-CX: root now lands on the rebuilt /dashboard
                          instead of /p/{HSQ_LANDING_FOLDER}. Legacy /p/:folder
                          routes are preserved for the "Legacy view" link in
                          the top bar. */}
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/p/:folder" element={<PageView />} />
                      {/* AGENT-CX: catch-all also points at /dashboard so
                          stale URLs / typos converge on the modern view. */}
                      <Route
                        path="*"
                        element={<Navigate to="/dashboard" replace />}
                      />
                    </Routes>
                  </main>
                </div>
                {/* AGENT-R: data-drill drawer — also listens for the
                    hsq:open-raw-tables window event dispatched by the top bar. */}
                <DataDrillDrawer />
              </div>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
